import React, { useCallback, useEffect, useRef, useState } from "react";
import { Grid, MenuItem } from "@mui/material";
import { Field, useFormikContext } from "formik";
import { Select, TextField } from "app/components/FormikField/base";
import { FormattedMessage, useIntl } from "react-intl";
import PropTypes from "prop-types";
import FormikRoundCheckbox from "app/components/FormikRoundCheckbox";
import { IsSuperAdmin } from "app/components/Authorization";
import {
  DivActionStyles,
  FieldStyled,
  FormLabelStyled,
  GridActionStyled,
  GridHeaderStyled,
  GridPermissionsStyled,
  GridStyled,
  GridTableStyled,
  GridTitleStyled,
  LabelStyled,
  SpanFloatLeftStyled,
  SpanFloatRightStyled,
  TypographySubtitleStyled
} from "./CreateRoleForm.styles";
import { API, WIDGET, ENVIRONMENT } from "../../../../common/Authorization/entities";
const CreateRoleForm = (props) => {
  const { owners, resources } = props;
  const intl = useIntl();
  const { values } = useFormikContext();
  const usePrevious = (value) => {
    const ref = useRef();
    useEffect(() => {
      ref.current = value;
    });
    return ref.current;
  };
  const previousPermissions = usePrevious(values.permissions);
  const { setFieldValue } = useFormikContext();
  const handleCrossEntityHierarchy = useCallback(
    (entity, permission) => {
      if (resources[entity] && resources[entity].hierarchy[permission]) {
        const missingPermissions = resources[entity].hierarchy[permission].filter((role) => {
          return !values.permissions.includes(role);
        });
        if (missingPermissions.length > 0) {
          return missingPermissions;
        }
      }
      return [];
    },
    [resources, values.permissions]
  );
  const handleAddedPermission = useCallback(
    (addedPermission, currentPermissions) => {
      const [role, entity, permission] = addedPermission.split("_");
      if (permission === "VIEW") {
        const validPermissions = [
          ...currentPermissions.filter((item) => !item.includes(`${role}_${entity}`)),
          ...handleCrossEntityHierarchy(entity, permission),
          addedPermission
        ];
        setFieldValue("permissions", validPermissions);
        return;
      }
      if (permission === "EDIT") {
        const crossEntityHierarchy = handleCrossEntityHierarchy(entity, permission).filter((role2) => {
          const [hierarchyRole, hierarchyEntity] = role2.split("_");
          return !currentPermissions.includes(`${hierarchyRole}_${hierarchyEntity}_EDIT`);
        });
        const validPermissions = [
          ...currentPermissions.filter((item) => item !== `${role}_${entity}_VIEW`),
          ...crossEntityHierarchy,
          addedPermission
        ];
        setFieldValue("permissions", validPermissions);
      }
    },
    [setFieldValue, handleCrossEntityHierarchy]
  );
  const handleRemovedPermission = useCallback(
    (removedPermission, userPermissions) => {
      const currentPermissions = userPermissions;
      const [role, entity, permission] = removedPermission.split("_");
      if (permission === "VIEW") {
        const dependencies = {};
        for (const entity2 in resources) {
          const resource = resources[entity2];
          const filteredPermissions = Object.keys(resource.hierarchy).filter(
            (permission2) => resource.hierarchy[permission2].includes(removedPermission)
          );
          if (filteredPermissions.length > 0) {
            const filteredHierarchy = {};
            filteredPermissions.forEach((permission2) => {
              filteredHierarchy[permission2] = resource.hierarchy[permission2];
            });
            dependencies[entity2] = filteredHierarchy;
          }
        }
        Object.keys(dependencies)?.forEach((entity2) => {
          const dependency = dependencies[entity2];
          if (!Object.keys(dependency).includes("VIEW")) {
            currentPermissions.push(`${role}_${entity2}_VIEW`);
          }
          for (let i = currentPermissions.length - 1; i >= 0; i--) {
            if (currentPermissions[i] === `ROLE_${entity2}_EDIT`) {
              currentPermissions.splice(i, 1);
            }
          }
        });
        const validPermissions = currentPermissions.filter(
          (item) => item === `${role}_${entity}_VIEW` || !item.includes(`${role}_${entity}`)
        );
        setFieldValue("permissions", validPermissions);
      }
      if (permission === "EDIT") {
        const validPermissions = currentPermissions.filter(
          (item) => item === `${role}_${entity}_VIEW` || !item.includes(`${role}_${entity}`)
        );
        setFieldValue("permissions", validPermissions);
      }
      const defaultPermissions = [
        { view: "ROLE_CONNECTION_VIEW", edit: "ROLE_CONNECTION_EDIT" },
        { view: "ROLE_EMPLOYER_VIEW", edit: "ROLE_EMPLOYER_EDIT" }
      ];
      defaultPermissions.forEach((defaultPermission) => {
        const rule1 = removedPermission === defaultPermission.view.toString() && !currentPermissions.includes(defaultPermission.edit);
        const rule2 = removedPermission === defaultPermission.edit.toString() && !currentPermissions.includes(defaultPermission.view);
        if (rule1 || rule2) {
          setFieldValue("permissions", [...currentPermissions, removedPermission]);
        }
      });
    },
    [resources, setFieldValue]
  );
  useEffect(() => {
    if (typeof previousPermissions === "undefined") {
      return;
    }
    const addedPermission = values.permissions.filter((item) => !previousPermissions.includes(item));
    const removedPermission = previousPermissions.filter((item) => !values.permissions.includes(item));
    if (addedPermission.length > 0) {
      handleAddedPermission(addedPermission[0], values.permissions);
    }
    if (removedPermission.length > 0) {
      handleRemovedPermission(removedPermission[0], values.permissions);
    }
  }, [handleAddedPermission, handleRemovedPermission, previousPermissions, values.permissions]);
  const isDisabled = useCallback(
    (value) => {
      return !values.permissions?.includes(value);
    },
    [values.permissions]
  );
  const [isSuperAdmin] = useState(IsSuperAdmin());
  return /* @__PURE__ */ React.createElement(GridStyled, { container: true, spacing: 2 }, isSuperAdmin && /* @__PURE__ */ React.createElement(Grid, { container: true, item: true, xs: 12 }, /* @__PURE__ */ React.createElement(Grid, { item: true, xs: 6 }, /* @__PURE__ */ React.createElement(FormLabelStyled, null, /* @__PURE__ */ React.createElement(FormattedMessage, { id: "roles.owner.define" }))), /* @__PURE__ */ React.createElement(Grid, { sx: { display: "flex", justifyContent: "end" }, item: true, xs: 6 }, /* @__PURE__ */ React.createElement(FieldStyled, { color: "secondary", component: Select, fullWidth: true, name: "ownerId", required: true, variant: "outlined" }, /* @__PURE__ */ React.createElement(MenuItem, { disabled: true, value: "''" }, /* @__PURE__ */ React.createElement("span", null, intl.formatMessage({ id: "employers.owner" }).toUpperCase())), owners.map((owner) => /* @__PURE__ */ React.createElement(MenuItem, { key: owner.ownerId, value: owner.ownerId }, owner.ownerName))))), /* @__PURE__ */ React.createElement(GridStyled, { item: true, xs: 6 }, /* @__PURE__ */ React.createElement(
    FieldStyled,
    {
      color: "secondary",
      component: TextField,
      fullWidth: true,
      name: "name",
      placeholder: intl.formatMessage({ id: "roles.name" }).toUpperCase(),
      required: true,
      variant: "outlined"
    }
  )), /* @__PURE__ */ React.createElement(GridStyled, { item: true, xs: 6 }, /* @__PURE__ */ React.createElement(
    FieldStyled,
    {
      color: "secondary",
      component: TextField,
      fullWidth: true,
      name: "description",
      placeholder: intl.formatMessage({ id: "roles.description" }).toUpperCase(),
      required: true,
      variant: "outlined"
    }
  )), /* @__PURE__ */ React.createElement(GridHeaderStyled, { container: true }, /* @__PURE__ */ React.createElement(GridTitleStyled, { xs: 8, item: true }, /* @__PURE__ */ React.createElement(FormattedMessage, { id: "roles.define" })), /* @__PURE__ */ React.createElement(GridActionStyled, { xs: 2, item: true }, /* @__PURE__ */ React.createElement(FormattedMessage, { id: "roles.view" })), /* @__PURE__ */ React.createElement(GridActionStyled, { xs: 2, item: true }, /* @__PURE__ */ React.createElement(FormattedMessage, { id: "roles.edit" }))), Object.entries(resources).map(([entity, options]) => {
    return /* @__PURE__ */ React.createElement(GridTableStyled, { container: true, key: entity }, /* @__PURE__ */ React.createElement(Grid, { xs: 8, item: true }, /* @__PURE__ */ React.createElement(TypographySubtitleStyled, null, /* @__PURE__ */ React.createElement(FormattedMessage, { id: `roles.entities.${entity}` })), /* @__PURE__ */ React.createElement(
      GridPermissionsStyled,
      {
        container: true,
        disabled: isDisabled(`ROLE_${entity}_EDIT`) && entity !== API && entity !== WIDGET && entity !== ENVIRONMENT
      },
      options.permissions.filter((item) => !["VIEW", "EDIT"].includes(item)).map((option) => /* @__PURE__ */ React.createElement(Grid, { xs: 12, md: 12, item: true, key: option }, /* @__PURE__ */ React.createElement(DivActionStyles, null, /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement(LabelStyled, null, /* @__PURE__ */ React.createElement(SpanFloatLeftStyled, null, /* @__PURE__ */ React.createElement(FormattedMessage, { id: `roles.permissions.${option}` })), /* @__PURE__ */ React.createElement(
        Field,
        {
          type: "checkbox",
          name: "permissions",
          value: `ROLE_${entity}_${option}`,
          label: `roles.permissions.${option}`,
          disabled: isDisabled(`ROLE_${entity}_EDIT`) && entity !== API && entity !== WIDGET && entity !== ENVIRONMENT
        }
      ), /* @__PURE__ */ React.createElement(SpanFloatRightStyled, null))))))
    )), options.permissions.filter((item) => ["VIEW", "EDIT"].includes(item)).map((option) => /* @__PURE__ */ React.createElement(Grid, { xs: 2, item: true, key: option }, /* @__PURE__ */ React.createElement(FormikRoundCheckbox, { name: "permissions", value: `ROLE_${entity}_${option}` }))));
  }));
};
CreateRoleForm.propTypes = {
  initialValues: PropTypes.object,
  permissions: PropTypes.object
};
export default CreateRoleForm;
